import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// Unicorn Studio 타입 선언
declare global {
  interface Window {
    UnicornStudio?: {
      init: () => void;
      isInitialized?: boolean;
    };
  }
}

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1.5;
  pointer-events: none;
  overflow: hidden;
  display: var(--webglDisplay, block);
`;

type Props = {
  projectId: string;
  width?: number;
  height?: number;
  opacity?: number;
};

function UnicornStudioRenderer({ projectId, width = 1440, height = 900, opacity = 1 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (!projectId || !containerRef.current) return;

    // Unicorn Studio 스크립트 로드
    const loadUnicornStudio = () => {
      if (window.UnicornStudio?.isInitialized) {
        // 이미 초기화되어 있으면 즉시 init
        window.UnicornStudio.init();
        return;
      }

      if (scriptLoadedRef.current) return;

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.5.3/dist/unicornStudio.umd.js';
      script.onload = () => {
        if (window.UnicornStudio && !window.UnicornStudio.isInitialized) {
          window.UnicornStudio.init();
          window.UnicornStudio.isInitialized = true;
        }
      };
      (document.head || document.body).appendChild(script);
      scriptLoadedRef.current = true;
    };

    loadUnicornStudio();

    // 컴포넌트 언마운트 시 정리
    return () => {
      // Unicorn Studio 인스턴스 정리 (필요시)
      if (containerRef.current) {
        const usDiv = containerRef.current.querySelector('[data-us-project]');
        if (usDiv) {
          usDiv.remove();
        }
      }
    };
  }, [projectId]);

  return (
    <Container ref={containerRef} style={{ opacity }}>
      <div
        data-us-project={projectId}
        style={{
          width: '100%',
          height: '100%',
          minWidth: `${width}px`,
          minHeight: `${height}px`,
        }}
      />
    </Container>
  );
}

export default UnicornStudioRenderer;

